import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from "@angular/core";
import { TenantSettings } from "../../domain/tenant-settings";
import { UserService } from "../../domain/user-service";
import { UI } from "../../services/ui.service";
import { TenantSettingsEnum } from "../../domain/enums";
import { Location } from "@angular/common";
import { UserRepository } from "../../domain/user-repository";
import { TenantService } from "../../services/tenant.service";
import { TagDependencyEntry, TagRepository } from "../../domain/tag-repository";
import { Tag } from "../../domain/tag";
import { IMediaTranscoderRepository } from "../../domain/media-transcoder/media-transcoder.repository";
import { TranscodingDestination } from "../../domain/media-transcoder/transcoding-destination";
import { TranscodingProfile } from "../../domain/media-transcoder/transcoding-profile";
import { MatTabChangeEvent } from "@angular/material/tabs";
import {
  ContentControlAIObjectDetectionSettings as ContentControlAIdetectionsSettings,
} from '../../domain/content-control/ai-settings';
import {
  ContentControlledEventActionEnum,
} from '../../domain/content-control/enums';
import { ICommonUtilInterface } from "../../domain/common/common-util.interface";
import { IEvrideoDialogService } from "../../domain/common/i-evrideo-dialog-service";
import { TenantSettingsSelectAiModelComponent } from "./tenant-settings-select-ai-model/tenant-settings-select-ai-model.component";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { ContentControlledEditingDirectiveInstructions } from '../../domain/content-control/editing-directive';
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import {
  ChangeToApprove,
  ETenantSettingActionType,
} from "./tenant-settings/actions/tenant-setting-change";
import { InsertTagChangeToApprove } from "./tenant-settings/actions/insert-tag-change-to-approve";
import { UpdateTagChangeToApprove } from "./tenant-settings/actions/update-tag-change-to-approve";
import { DeleteTagChangeToApprove } from "./tenant-settings/actions/delete-tag-change-to-approve";
import { UpdateTenantSettingChangeToApprove } from "./tenant-settings/actions/update-tenant-setting-change-to-approve";
import { UpdateMetaDataChangeToApprove } from "./tenant-settings/actions/update-meta-data-change-to-approve";
import { ExecuteActionResult } from "./tenant-settings/actions/execute-action-result";
import { QCWorkflowObjective } from "../../domain/qc-workflow";
import { DeleteQCWorkflowObjectiveChangeToApprove } from "./tenant-settings/actions/delete-qc-workflow-objective-change-to-approve";
import { InsertQCWorkflowObjectiveChangeToApprove } from "./tenant-settings/actions/insert-qc-workflow-objective-change-to-approve";
import { UpdateQCWorkflowObjectiveChangeToApprove } from "./tenant-settings/actions/update-qc-workflow-objective-change-to-approve";
import {
  AIVideoAnalyzeConfig, EAiModelType,
  IAIObjectDetectionClass
} from '../../domain/content-control/ai';
import { User } from "../../domain/user";
import {
  Destination,
  DestinationDetails,
  DestinationDisconnectResult,
  DestinationPlatform,
  DestinationPlatformData,
  DestinationPlatformWithDestinations,
  UpdateDestinationRequest,
  isTikTokPlatform as isTikTokCatalogPlatform,
  YouTubeVideoCategoryCatalog,
} from "../../domain/destinations";
import { EvrideoDialogOptions } from "../common/evrideo-dialog/evrideo-dialog.component";
import { DestinationsRepository } from "../../infrastructure/repositories/destinations/destinations.repository";
import { firstValueFrom } from "rxjs";
import {
  DestinationStatusMeta,
  DESTINATION_STATUS_META,
} from "../destinations/destinations.constants";
import { DestinationSettingsPanelState } from "../destinations/destination-settings/destination-settings-panel.component";

export interface DestinationSettingsUiState {
  expanded: boolean;
  panelState: DestinationSettingsPanelState;
  details: DestinationDetails | null;
  videoCategories: YouTubeVideoCategoryCatalog | null;
  errorMessage: string | null;
  draftPlatformData: DestinationPlatformData | null;
  isSaving: boolean;
}

function createIdleDestinationSettingsState(): DestinationSettingsUiState {
  return {
    expanded: false,
    panelState: "idle",
    details: null,
    videoCategories: null,
    errorMessage: null,
    draftPlatformData: null,
    isSaving: false,
  };
}

function cloneDestinationPlatformData(
  data: DestinationPlatformData | null,
): DestinationPlatformData | null {
  if (data == null) {
    return null;
  }
  return { ...data };
}

interface TabContent {
  label: string;
  categoriesTemplate: TemplateRef<any>;
  formTemplate: TemplateRef<any>;
}

const DISCONNECT_RESULT_DIALOG_TIMEOUT_MS = 3000;

@Component({
  selector: "tenant-settings",
  templateUrl: "./tenant-settings.component.html",
  styleUrls: ["./tenant-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantSettingsComponent implements OnInit {
  @ViewChildren("form") forms!: QueryList<ElementRef>;
  @ViewChildren("categories") categories!: QueryList<ElementRef>;

  public tenantSettings: TenantSettings;
  private updateTenantSettingsChanges: UpdateTenantSettingChangeToApprove[] =
    [];
  private updateMetadataChanges: UpdateMetaDataChangeToApprove[] = [];
  private get changesToApprove(): ChangeToApprove[] {
    return this.tenantSettingsChangesToApprove
      .concat(this.updateMetadataChanges)
      .concat(this.tagsMarkedToUpdate)
      .concat(this.tagsMarkedToInsert)
      .concat(this.tagsMarkedToDelete);
  }
  private get tenantSettingsChangesToApprove(): ChangeToApprove[] {
    return this.updateTenantSettingsChanges.concat(
      this.QCWorkflowObjectivesChangesToApprove,
    );
  }
  private get QCWorkflowObjectivesChangesToApprove(): ChangeToApprove[] {
    return this.qcWorkflowObjectivesMarkedToDelete
      .concat(this.qcWorkflowObjectivesMarkedToUpdate)
      .concat(this.qcWorkflowObjectivesMarkedToInsert);
  }

  tenantMetadata: { id: number; name: string }[] = [];
  newMetadataName = "";
  newTagName = "";
  newQCWorkflowObjectiveTask = "";
  tenantName = "";
  saveChangesProcessing = false;
  metaDataMarkedToDelete = [];
  tagsMarkedToDelete: DeleteTagChangeToApprove[] = [];
  tagsMarkedToUpdate: UpdateTagChangeToApprove[] = [];
  tagsMarkedToInsert: InsertTagChangeToApprove[] = [];
  updatedMetaDataNames: { id: number; name: string }[] = [];
  qcWorkflowObjectivesMarkedToDelete: DeleteQCWorkflowObjectiveChangeToApprove[] =
    [];
  qcWorkflowObjectivesMarkedToUpdate: UpdateQCWorkflowObjectiveChangeToApprove[] =
    [];
  qcWorkflowObjectivesMarkedToInsert: InsertQCWorkflowObjectiveChangeToApprove[] =
    [];
  availableQCWorkflowObjectives: QCWorkflowObjective[] = [];
  currentUser: User;
  private title = "Tenant Settings";
  availabilityZones: { id: number; displayName: string; previewUrl: string }[] =
    [];
  tags: Tag[] = [];
  availableTags: Tag[] = [];

  daysOfWeek = [
    {
      id: 0,
      name: "Sunday",
    },
    {
      id: 1,
      name: "Monday",
    },
    {
      id: 2,
      name: "Tuesday",
    },
    {
      id: 3,
      name: "Wednesday",
    },
    {
      id: 4,
      name: "Thursday",
    },
    {
      id: 5,
      name: "Friday",
    },
    {
      id: 6,
      name: "Saturday",
    },
  ];

  get editableTags(): Tag[] {
    return this.availableTags.concat(
      this.tagsMarkedToInsert.flatMap((c) => c.tag),
    );
  }
  get editableQCWorkflowObjectives(): QCWorkflowObjective[] {
    return this.availableQCWorkflowObjectives
      .concat(
        this.qcWorkflowObjectivesMarkedToInsert.flatMap((c) => c.objective),
      )
      .sort((a, b) => {
        if (a.isDeleted !== b.isDeleted) {
          return a.isDeleted ? 1 : -1;
        }
        return a.id - b.id;
      });
  }
  contentControlTranscodingDestinations: TranscodingDestination[] = [];
  contentControlTranscodingProfiles: TranscodingProfile[] = [];
  /** Merged catalog + tenant rows from `DestinationsRepository` (API). */
  destinationPlatforms: DestinationPlatformWithDestinations[] = [];
  /** Per-destination expand/load state for the settings panel (key = destination uid). */
  private readonly destinationSettingsByUid = new Map<string, DestinationSettingsUiState>();
  /** When true, render the "Add Destination" button for each platform. */
  canAddDestinations = false;
  /** When true, render the disconnect (trash) action for each destination row. */
  canDisconnectDestinations = false;
  /** When true, destination settings fields are editable and Save is shown. */
  canEditDestinationSettings = false;
  /** True while authorize-url API is in flight; all "Add Destination" buttons are disabled. */
  isAddingDestination = false;

  tabs: TabContent[] = [];
  @ViewChild("contentControlSettingsCategoriesTemplate")
  contentControlSettingsCategoriesTemplate!: TemplateRef<any>;
  @ViewChild("contentControlSettingsFormTemplate")
  contentControlSettingsFormTemplate!: TemplateRef<any>;
  @ViewChild("clippingStudioSettingsCategoriesTemplate")
  clippingStudioSettingsCategoriesTemplate!: TemplateRef<any>;
  @ViewChild("clippingStudioSettingsFormTemplate")
  clippingStudioSettingsFormTemplate!: TemplateRef<any>;
  @ViewChild("generalSettingsCategoriesTemplate")
  generalSettingsCategoriesTemplate!: TemplateRef<any>;
  @ViewChild("generalSettingsFormTemplate")
  generalSettingsFormTemplate!: TemplateRef<any>;
  @ViewChild("destinationsSettingsCategoriesTemplate")
  destinationsSettingsCategoriesTemplate!: TemplateRef<any>;
  @ViewChild("destinationsSettingsFormTemplate")
  destinationsSettingsFormTemplate!: TemplateRef<any>;
  activeTabFormElement: ElementRef | null = null;
  constructor(
    private tenantSettingsService: TenantService,
    private ui: UI,
    private changeDetectorRef: ChangeDetectorRef,
    private location: Location,
    private userService: UserService,
    private tagRepository: TagRepository,
    private userRepository: UserRepository,
    private _mediaTranscodingRepository: IMediaTranscoderRepository,
    private _commonUtils: ICommonUtilInterface,
    private _dialogService: IEvrideoDialogService,
    private viewContainerRef: ViewContainerRef,
    private _sanitizer: DomSanitizer,
    private _destinationsRepository: DestinationsRepository,
    private _toastr: ToastrService,
    private _route: ActivatedRoute,
    private _router: Router,
  ) {}

  async ngOnInit() {
    this.availableTags = this.tagRepository.getGeneralTags();
    this.availableTags = this.availableTags.sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    this.contentControlTranscodingDestinations =
      await this._mediaTranscodingRepository.getTenantTranscodingDestinations();
    this.contentControlTranscodingProfiles =
      await this._mediaTranscodingRepository.getProfiles();

    this.loadTenantSettings();
    const currentUser = this.userService.getCurrentUser();
    this.tenantName = currentUser.managedTenants.find(
      (x) => x.id === currentUser.currentTenantId,
    ).name;
    this.tenantMetadata = currentUser.metadata;
    this.currentUser = currentUser;
    this.availabilityZones = currentUser.channelAvailabilityZones.filter(
      (z) => z.isActive,
    );

    const tabs = [];

    if (this.currentUser.isSuperUser) {
      tabs.push({
        label: "General Settings",
        categoriesTemplate: this.generalSettingsCategoriesTemplate,
        formTemplate: this.generalSettingsFormTemplate,
      });
    }

    if (this.currentUser.isSuperUser || this.currentUser.isContentControlManager){
      tabs.push({
        label: "Content Control Settings",
        categoriesTemplate: this.contentControlSettingsCategoriesTemplate,
        formTemplate: this.contentControlSettingsFormTemplate,
      });
      tabs.push({
        label: "Clipping Studio Settings",
        categoriesTemplate: this.clippingStudioSettingsCategoriesTemplate,
        formTemplate: this.clippingStudioSettingsFormTemplate,
      });
    }

    if (this.currentUser.hasDestinationsViewerPermission) {
      tabs.push({
        label: "Destinations",
        categoriesTemplate: this.destinationsSettingsCategoriesTemplate,
        formTemplate: this.destinationsSettingsFormTemplate,
      });
    }
    this.tabs = tabs;

    this.canAddDestinations = !!this.currentUser.hasDestinationsManagerPermission;
    this.canDisconnectDestinations =
      !!this.currentUser.hasDestinationsManagerPermission;
    this.canEditDestinationSettings =
      !!this.currentUser.hasDestinationsEditorPermission;

    if (this.currentUser.hasDestinationsViewerPermission) {
      try {
        this.destinationPlatforms = await firstValueFrom(
          this._destinationsRepository.getPlatforms(),
        );
      } catch {
        this.destinationPlatforms = [];
      }
      await this.handleOAuthCallbackQueryParams();
    }

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Starts the OAuth connect flow for the given catalog platform. Backend resolves
   * the provider URL; the browser is then redirected there. Only platforms with a
   * registered backend `IPlatformOAuthService` will succeed.
   */
  async onAddDestination(platformId: number): Promise<void> {
    if (!this.canAddDestinations || this.isAddingDestination) {
      return;
    }

    this.isAddingDestination = true;

    try {
      const returnUrl = `${window.location.origin}/tenantsettings`;
      const url = await this._destinationsRepository.getAuthorizeUrl(
        platformId,
        returnUrl,
      );
      window.location.href = url;
    } catch {
      this.isAddingDestination = false;
      this._toastr.error(
        "Failed to start the connect flow for this platform.",
      );
    }
  }

  async onDisconnectDestination(
    platform: DestinationPlatform,
    destination: Destination,
  ): Promise<void> {
    if (!this.canDisconnectDestinations) {
      return;
    }

    const confirmed = await this._dialogService.openDialog(
      this.viewContainerRef,
      {
        type: "warning",
        title: "Disconnect destination",
        content: `Are you sure you want to disconnect destination with name "<strong>${destination.displayName}</strong>"?`,
        confirmButtonText: "Disconnect",
        cancelButtonText: "Cancel",
      },
    );
    if (!confirmed) {
      return;
    }

    const disconnectWork = (async (): Promise<DestinationDisconnectResult> => {
      const payload = await this._destinationsRepository.disconnectDestination(
        platform.platformId,
        destination.uid,
      );
      await this._destinationsRepository.refreshTenantPlatformsWithDestinations();
      this.destinationPlatforms = await firstValueFrom(
        this._destinationsRepository.getPlatforms(),
      );
      this.destinationSettingsByUid.delete(destination.uid);
      this.changeDetectorRef.markForCheck();
      return payload;
    })();

    await this._dialogService.openDialogWithLoading(
      this.viewContainerRef,
      {
        title: "Disconnect destination",
        loadingContent: "Disconnecting destination...",
      },
      disconnectWork,
      (payload) =>
        this.buildDisconnectResultDialogOptions(
          payload as DestinationDisconnectResult,
        ),
      () => this.buildDisconnectErrorDialogOptions(),
    );
  }

  private buildDisconnectResultDialogOptions(
    payload: DestinationDisconnectResult,
  ): EvrideoDialogOptions {
    const autoCloseOptions: Pick<EvrideoDialogOptions, "timeout"> = {
      timeout: DISCONNECT_RESULT_DIALOG_TIMEOUT_MS,
    };

    if (payload.token_revoked) {
      return {
        type: "success",
        title: "Disconnected",
        content: "Destination disconnected successfully.",
        ...autoCloseOptions,
      };
    }

    return {
      type: "warning",
      title: "Disconnected with warning",
      content:
        payload.message ??
        "Destination was removed locally, but provider token revocation failed.",
      ...autoCloseOptions,
    };
  }

  private buildDisconnectErrorDialogOptions(): EvrideoDialogOptions {
    return {
      type: "error",
      title: "Disconnect failed",
      content:
        "Could not disconnect the destination. Please try again later.",
      timeout: DISCONNECT_RESULT_DIALOG_TIMEOUT_MS,
    };
  }

  /**
   * If we just returned from the OAuth callback, continue the matching frontend
   * flow. One-step providers finish here; multi-step providers may use
   * `selection_required` when a dedicated selection UI exists.
   */
  private async handleOAuthCallbackQueryParams(): Promise<void> {
    const params = this._route.snapshot.queryParamMap;
    const status = params.get("status");
    if (
      status !== "success" &&
      status !== "error" &&
      status !== "selection_required"
    ) {
      return;
    }

    if (status === "success") {
      try {
        await this._destinationsRepository.refreshTenantPlatformsWithDestinations();
        this.destinationPlatforms = await firstValueFrom(
          this._destinationsRepository.getPlatforms(),
        );
      } catch {
        // Refresh failure is non-fatal; the user can reload to retry.
      }
      this._toastr.success("Destination connected.");
    } else if (status === "error") {
      const code = params.get("code");
      this._toastr.error(`Connect failed: ${code ?? "unknown_error"}`);
    } else {
      this._toastr.error(
        "Connect requires an additional selection step that is not configured for this platform.",
      );
    }

    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  /** DOM id for scrolling to a platform section (must match fieldset id). */
  destinationsPlatformSectionId(platformId: number): string {
    return `destinations-settings-platform-${platformId}`;
  }

  /** Aggregates from `destinationPlatforms` (summary counts; no change %). */
  get destinationsOverviewConnectedAccounts(): number {
    return this.destinationPlatforms.reduce((sum, row) => sum + row.destinations.length, 0);
  }

  get destinationsOverviewActiveEndpoints(): number {
    return this.destinationPlatforms.reduce(
      (sum, row) =>
        sum + row.destinations.filter((d) => d.status === "Connected").length,
      0,
    );
  }

  get destinationsOverviewNeedsAttention(): number {
    return this.destinationPlatforms.reduce(
      (sum, row) =>
        sum + row.destinations.filter((d) => d.status !== "Connected").length,
      0,
    );
  }

  get destinationsOverviewSupportedPlatforms(): number {
    return this.destinationPlatforms.length;
  }

  destinationAccountStatusMeta(destination: Destination): DestinationStatusMeta {
    return DESTINATION_STATUS_META[destination.status];
  }

  isTikTokPlatform(platform: DestinationPlatform): boolean {
    return isTikTokCatalogPlatform(platform);
  }

  canShowDestinationSettings(platform: DestinationPlatform): boolean {
    return !this.isTikTokPlatform(platform);
  }

  destinationSettingsState(destinationUid: string): DestinationSettingsUiState {
    return (
      this.destinationSettingsByUid.get(destinationUid) ??
      createIdleDestinationSettingsState()
    );
  }

  async toggleDestinationSettings(
    platform: DestinationPlatform,
    destination: Destination,
  ): Promise<void> {
    const uid = destination.uid;
    const current =
      this.destinationSettingsByUid.get(uid) ?? createIdleDestinationSettingsState();

    if (current.expanded) {
      this.destinationSettingsByUid.set(uid, {
        ...current,
        expanded: false,
      });
      this.changeDetectorRef.markForCheck();
      return;
    }

    for (const [otherUid, state] of this.destinationSettingsByUid.entries()) {
      if (otherUid !== uid && state.expanded) {
        this.destinationSettingsByUid.set(otherUid, {
          ...state,
          expanded: false,
        });
      }
    }

    this.destinationSettingsByUid.set(uid, {
      ...createIdleDestinationSettingsState(),
      expanded: true,
      panelState: "loading",
    });
    this.changeDetectorRef.markForCheck();

    try {
      const result = await this._destinationsRepository.loadDestinationSettings(
        platform,
        uid,
      );
      this.destinationSettingsByUid.set(uid, {
        expanded: true,
        panelState: "ready",
        details: result.details,
        videoCategories: result.videoCategories,
        errorMessage: null,
        draftPlatformData: cloneDestinationPlatformData(
          result.details.platformData,
        ),
        isSaving: false,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not load destination settings.";
      this.destinationSettingsByUid.set(uid, {
        expanded: true,
        panelState: "error",
        details: null,
        videoCategories: null,
        errorMessage: message,
        draftPlatformData: null,
        isSaving: false,
      });
      this._toastr.error("Could not load destination settings.");
    }

    this.changeDetectorRef.markForCheck();
  }

  onDestinationSettingsDraftChange(
    destinationUid: string,
    platformData: DestinationPlatformData,
  ): void {
    const current =
      this.destinationSettingsByUid.get(destinationUid) ??
      createIdleDestinationSettingsState();
    this.destinationSettingsByUid.set(destinationUid, {
      ...current,
      draftPlatformData: platformData,
    });
    this.changeDetectorRef.markForCheck();
  }

  async onSaveDestinationSettings(
    platform: DestinationPlatform,
    destination: Destination,
  ): Promise<void> {
    if (!this.canEditDestinationSettings) {
      return;
    }

    const uid = destination.uid;
    const state = this.destinationSettingsByUid.get(uid);
    if (!state?.details || state.panelState !== "ready") {
      return;
    }

    const tenantId = this.currentUser?.currentTenantId;
    if (tenantId == null || tenantId <= 0) {
      this._toastr.error("Could not determine the current tenant.");
      return;
    }

    const request: UpdateDestinationRequest = {
      tenant_id: tenantId,
      uid: state.details.uid,
      platform_id: platform.platformId,
      platform_data: state.draftPlatformData,
    };

    this.destinationSettingsByUid.set(uid, { ...state, isSaving: true });
    this.changeDetectorRef.markForCheck();

    try {
      await this._destinationsRepository.updateDestinationSettings(uid, request);
      this.destinationSettingsByUid.set(uid, {
        ...state,
        isSaving: false,
        details: {
          ...state.details,
          platformData: cloneDestinationPlatformData(state.draftPlatformData),
        },
      });
      this._toastr.success("Destination settings saved.");
    } catch {
      this.destinationSettingsByUid.set(uid, { ...state, isSaving: false });
      this._toastr.error(
        "Could not save destination settings. Please try again later.",
      );
    }

    this.changeDetectorRef.markForCheck();
  }

  ngAfterViewInit() {

  }

  loadTenantSettings() {
    this.tenantSettingsService.getTenantSettings().then((settings) => {
      console.log("loadTenantSettings-> settings", settings);
      this.tenantSettings = settings;

      this.tags = this.availableTags.filter((t) =>
        this.tenantSettings.NextShowSkipTagIds.includes(t.id),
      );
      this.availableQCWorkflowObjectives =
        this.tenantSettings.QCWorkflowObjectives;
      //this.tags = [];

      this.changeDetectorRef.markForCheck();
      setTimeout(() => {
        this.activeTabFormElement = this.forms.first.nativeElement;
      }, 0);
    });
  }
  onTabChange(event: MatTabChangeEvent): void {
    // Update the reference to the form based on the selected tab index
    const selectedIndex = event.index;
    const formArray = this.forms.toArray();

    if (formArray[selectedIndex]) {
      this.activeTabFormElement = formArray[selectedIndex];
    }
  }
  onCategoryClick(id: string, event: MouseEvent): void {
    const container =
      this.activeTabFormElement.nativeElement || this.forms.first.nativeElement;
    const element = container.querySelector(`#${id}`);

    if (element) {
      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const relativeTop =
        elementRect.top - containerRect.top + container.scrollTop;

      container.scrollTo({
        top: relativeTop,
        behavior: "smooth",
      });

      const clickedLi = event.target as HTMLElement;
      const listContainer = clickedLi.parentElement;

      if (listContainer) {
        Array.from(listContainer.children).forEach((li: Element) => {
          li.classList.remove("highlighted");
        });

        clickedLi.classList.add("highlighted");
      }

      // Optional: Add highlight effect to the scrolled element
      element.classList.add("content-highlighted");
      setTimeout(() => {
        element.classList.remove("content-highlighted");
      }, 1200);
    }
  }

  onDoQcAnalizeWhenUploadingChanged(): void {
    this.tenantSettings.DoQcAnalizeWhenUploading =
      !this.tenantSettings.DoQcAnalizeWhenUploading;

    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.DoQcAnalyzeWhenUploading,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onAllowRecordingFeatureChanged(): void {
    this.tenantSettings.AllowRecordingFeature =
      !this.tenantSettings.AllowRecordingFeature;

    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.AllowRecordingFeature,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onUseMediaStartTimestampInSchedulerChanged(): void {
    this.tenantSettings.UseMediaStartTimestampInScheduler =
      !this.tenantSettings.UseMediaStartTimestampInScheduler;

    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.UseMediaStartTimestampInScheduler,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }
  onShowMediaLicenseSettingsChanged(): void {
    this.tenantSettings.ShowMediaLicenseSettings =
      !this.tenantSettings.ShowMediaLicenseSettings;

    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ShowMediaLicenseSettings,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onFuturePreviewLimitChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.FuturePreviewChannelsLimit,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onTranscodingAvailabilityZoneChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.AvailabilityZoneForTranscoding,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onTranscodingProfileChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ContentControlTranscodingProfile,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onTranscodingDestinationChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ContentControlTranscodingDestination,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onClippingStudioTranscodingDestinationChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ClippingStudioTranscodingDestination,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onLiveFeedPreviewAvailabilityZoneChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.AvailabilityZoneForLiveFeedPreview,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onTranscodeBypassFeatureChanged(): void {
    this.tenantSettings.ByPassWithGeneratingLowRes = false;

    this.tenantSettings.TranscodeBypassFeatureEnabled =
      !this.tenantSettings.TranscodeBypassFeatureEnabled;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.TranscodeBypassFeatureEnabled,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onByPassWithGeneratingLowResChanged(): void {
    this.tenantSettings.ByPassWithGeneratingLowRes =
      !this.tenantSettings.ByPassWithGeneratingLowRes;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ByPassWithGeneratingLowRes,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onLongShowColorChanged(newVal: any): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(TenantSettingsEnum.LongShowColor),
    );
    this.changeDetectorRef.markForCheck();
  }

  onLongShowSecondsChanged(): void {
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.LongShowSeconds,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onDeleteOriginalFilesChanged(): void {
    if (!this.tenantSettings.DeleteOriginalMediaFiles) {
      this.buildConfirmPopup(
        "Are you sure? this will delete all original files. Your transcoded files will be kept.",
        [
          {
            title: "Cancel",
            callback: undefined,
          },
          {
            title: "Approve",
            callback: () => {
              this.onDeleteOriginalFilesConfirmed();
            },
            theme: "WARNING",
          },
        ],
      );
    } else {
      this.tenantSettings.DeleteOriginalMediaFiles = false;
    }
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.DeleteOriginalMediaFiles,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }

  onDeleteOriginalFilesConfirmed() {
    this.tenantSettings.DeleteOriginalMediaFiles = true;
    this.changeDetectorRef.markForCheck();
  }
  onBroadcastsPreviewChannelStaticRendererSuffixChanged(value: string): void {
    this.tenantSettings.BroadcastsPreviewChannelStaticRendererSuffix = value;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(TenantSettingsEnum.BroadcastsPreviewChannelStaticRendererSuffix),
    );
    this.changeDetectorRef.markForCheck();
  }
  onBroadcastsProgramChannelStaticRendererSuffixChanged(value: string): void {
    this.tenantSettings.BroadcastsProgramChannelStaticRendererSuffix = value;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(TenantSettingsEnum.BroadcastsProgramChannelStaticRendererSuffix),
    );
    this.changeDetectorRef.markForCheck();
  }

  pushTenantSettingsChangeToApprove(change: ChangeToApprove) {
    if (
      !this.changesToApprove.find(
        (c) =>
          c.actionType === change.actionType && c.message === change.message,
      )
    ) {
      this.updateTenantSettingsChanges.push(change);
    }
  }

  pushTenantSettingsChangeToApproveWithoutMessage(setting: TenantSettingsEnum) {
    this.pushTenantSettingsChangeToApprove(new UpdateTenantSettingChangeToApprove(setting));
  }

  async onSaveChanges() {
    if (
      this.saveChangesProcessing === false &&
      this.changesToApprove.length > 0
    ) {
      this.saveChangesProcessing = true;

      const handleTagsPromise = this._handleTagsCRUD();
      const handleUpdateTenantSettingsPromise =
        this._handleTenantSettingsUpdate();
      const handleUpdateMetadataPromise = this._handleUpdateMetadata();

      const executeActionsResults = (
        await Promise.all([
          handleTagsPromise,
          handleUpdateTenantSettingsPromise,
          handleUpdateMetadataPromise,
        ])
      ).flatMap((x) => x);
      this._showResultPopup(executeActionsResults);

      this.saveChangesProcessing = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  onCancel() {
    if (this.changesToApprove.length > 0) {
      const message = `Are you sure you want to cancel?<br/>Changes you've made will not be saved<br/>`;
      this.buildConfirmPopup(message, [
        {
          title: "Cancel Changes",
          callback: () => {
            this.updateTenantSettingsChanges = [];
            this.loadTenantSettings();
          },
        },
        {
          title: "Apply Changes",
          callback: () => {
            this.onSaveChanges().then();
          },
        },
      ]);
    } else {
      this.location.back();
    }
  }

  buildConfirmPopup(message, buttons) {
    this.ui.notify(this.ui.EVENTS.ShowConfirm, {
      title: this.title,
      message: message,
      buttons: buttons,
    });
  }

  buildChangesList(message): string {
    const distinctChanges = [
      ...new Set(this.changesToApprove.map((c) => c.message)),
    ];
    for (let i = 0; i < distinctChanges.length; i++) {
      message += `<div > ${distinctChanges[i]}</div>`;
    }
    return message;
  }
  onAddNewTag() {
    if (
      this.newTagName.trim().length > 0 &&
      !this.editableTags.find((t) => t.name.trim() === this.newTagName.trim())
    ) {
      const newTag = new Tag(
        false,
        this.availableTags.length + 1,
        this.newTagName,
      );
      this.tagsMarkedToInsert.push(
        new InsertTagChangeToApprove(
          newTag,
          "Add New Tag: " + this.newTagName.trim(),
        ),
      );
    }
    this.newTagName = "";
  }
  onAddNewMetadataName() {
    if (this.newMetadataName.trim().length >= 50) {
      this.ui.notify(this.ui.EVENTS.ShowAlert, {
        title: "Error",
        message: "metadata name length must be 50 characters or less",
      });
      return;
    }
    const tempName = this.newMetadataName;
    this.newMetadataName = "";
    if (tempName.trim().length === 0) {
      return;
    }
    const metadataIndex = this.tenantMetadata.findIndex(
      (x) => x.name.toUpperCase() === tempName.trim().toUpperCase(),
    );
    const deletedIndex = this.metaDataMarkedToDelete.findIndex(
      (x) => x.name.toUpperCase() === tempName.trim().toUpperCase(),
    );
    if (metadataIndex >= 0 && deletedIndex >= 0) {
      this.metaDataMarkedToDelete.splice(deletedIndex, 1);
      const changeIndex = this.changesToApprove.findIndex(
        (x) =>
          x.message.toUpperCase().indexOf(tempName.trim().toUpperCase()) >= 0,
      );
      if (changeIndex >= 0) {
        this.changesToApprove.splice(changeIndex, 1);
      }
      return;
    }
    if (metadataIndex < 0) {
      this.tenantMetadata.push({ id: -1, name: tempName.trim() });
      const changeIndex = this.changesToApprove.findIndex(
        (x) =>
          x.message.toUpperCase().indexOf(tempName.trim().toUpperCase()) >= 0,
      );
      if (changeIndex >= 0) {
        this.changesToApprove.splice(changeIndex, 1);
      }

      this.updateMetadataChanges.push(
        new UpdateMetaDataChangeToApprove(
          "Add New Metadata Name: " + tempName.trim(),
        ),
      );
    }
  }
  onAddNewQCWorkflowObjective() {
    if (
      this.newQCWorkflowObjectiveTask.trim().length > 0 &&
      !this.editableQCWorkflowObjectives.find(
        (t) => t.task.trim() === this.newQCWorkflowObjectiveTask.trim(),
      )
    ) {
      const newQCWorkflowObjective = new QCWorkflowObjective(
        this.editableQCWorkflowObjectives.length + 1,
        this.newQCWorkflowObjectiveTask,
      );
      this.qcWorkflowObjectivesMarkedToInsert.push(
        new InsertQCWorkflowObjectiveChangeToApprove(
          newQCWorkflowObjective,
          "Add New QC Workflow Objective: " +
            this.newQCWorkflowObjectiveTask.trim(),
        ),
      );
    }
    this.newQCWorkflowObjectiveTask = "";
  }
  deleteTagClicked(tag: Tag) {
    if (this.tagsMarkedToInsert.find((t) => t.tag === tag)) {
      this.tagsMarkedToInsert.splice(
        this.tagsMarkedToInsert.findIndex((t) => t.tag === tag),
        1,
      );
      this.tagsMarkedToInsert.splice(
        this.changesToApprove.findIndex(
          (c) => c.message === "Add New Tag: " + tag.name.trim(),
        ),
        1,
      );
    } else {
      if (this.tagsMarkedToUpdate.find((t) => t.tag === tag)) {
        this.tagsMarkedToUpdate = this.tagsMarkedToUpdate.filter(
          (t) => t.tag !== tag,
        );
        //this.changesToApprove = this.changesToApprove.filter(c => (c.message.includes("Update Tag From") && c.sourceId === tag.id) === false);
      }
      this.tagsMarkedToDelete.push(
        new DeleteTagChangeToApprove(tag, "Delete Tag: " + tag.name),
      );
    }
  }
  deleteQCWorkflowObjectiveClicked(objective: QCWorkflowObjective) {
    if (
      this.qcWorkflowObjectivesMarkedToInsert.find(
        (t) => t.objective.id === objective.id,
      )
    ) {
      this.qcWorkflowObjectivesMarkedToInsert.splice(
        this.qcWorkflowObjectivesMarkedToInsert.findIndex(
          (t) => t.objective.id === objective.id,
        ),
        1,
      );
      this.changesToApprove.splice(
        this.changesToApprove.findIndex((c) => c.sourceId === objective.id),
        1,
      );
    } else {
      if (
        this.qcWorkflowObjectivesMarkedToUpdate.find(
          (t) => t.objective === objective,
        )
      ) {
        this.qcWorkflowObjectivesMarkedToUpdate =
          this.qcWorkflowObjectivesMarkedToUpdate.filter(
            (t) => t.objective.id !== objective.id,
          );
      }
      this.qcWorkflowObjectivesMarkedToDelete.push(
        new DeleteQCWorkflowObjectiveChangeToApprove(
          objective,
          "Delete QC Workflow Objective: " + objective.task,
        ),
      );
    }
  }
  toggleDeleteMetadata(metadataItem) {
    const index = this.metaDataMarkedToDelete.findIndex(
      (x) => x.name === metadataItem.name,
    );
    if (index >= 0) {
      this.metaDataMarkedToDelete.splice(index, 1);
      const changeIndex = this.changesToApprove.findIndex(
        (x) =>
          x.message.toUpperCase().indexOf(metadataItem.name.toUpperCase()) >= 0,
      );
      if (changeIndex >= 0) {
        this.changesToApprove.splice(changeIndex, 1);
      }
    } else {
      this.metaDataMarkedToDelete.push(metadataItem);
      const changeIndex = this.changesToApprove.findIndex(
        (x) =>
          x.message.toUpperCase().indexOf(metadataItem.name.toUpperCase()) >= 0,
      );
      if (changeIndex >= 0) {
        this.changesToApprove.splice(changeIndex, 1);
      }

      this.updateMetadataChanges.push(
        new UpdateMetaDataChangeToApprove(
          "Delete Metadata Name: " + metadataItem.name,
        ),
      );
    }
  }

  isMarkedToAction(dataSet: { id: number }[], itemToCheck: { id: number }) {
    if (dataSet.findIndex((x) => x.id === itemToCheck.id) >= 0) {
      return true;
    }
    return false;
  }

  addNewTag(tag) {
    if (this.tags.indexOf(tag) < 0) {
      this.tags.push(tag);
      this.tenantSettings.NextShowSkipTagIds.push(tag.id);
      this.pushTenantSettingsChangeToApprove(
        new UpdateTenantSettingChangeToApprove(
          TenantSettingsEnum.NextShowSkipTags,
        ),
      );
    }
  }

  deleteExistTag(tag) {
    this.tags.forEach(
      ((savedTag, index) => {
        if (savedTag.id === tag.id) {
          this.tags.splice(index, 1);
        }
      }).bind(this),
    );

    const tagIndex = this.tenantSettings.NextShowSkipTagIds.indexOf(tag.id);
    this.tenantSettings.NextShowSkipTagIds.splice(tagIndex, 1);
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.NextShowSkipTags,
      ),
    );
  }

  createNewTag(tag) {
    if (tag.trim() === "") {
      return;
    }
    this.tagRepository
      .CreateGeneralTag(tag)
      .then(
        ((response) => {
          /* Updating UI */

          /* Updating Tags */
          if (this.tags.findIndex((tag) => tag.id === response.id) === -1) {
            this.addNewTag(response);
          }
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        }).bind(this),
        ((error) => {}).bind(this),
      )
      .catch(((error) => {}).bind(this));
  }
  onTagNameBlur(tag: Tag, event) {
    const newTagName = event.target.innerText;
    if (newTagName.trim() === tag.name.trim()) {
      return;
    }
    if (this.tagsMarkedToDelete.find((t) => t.tag === tag)) {
      event.target.innerText = tag.name;
      return;
    }

    if (!this.tagsMarkedToInsert.find((t) => t.tag === tag)) {
      this.tagsMarkedToUpdate.push(
        new UpdateTagChangeToApprove(
          tag,
          `Update Tag From ${tag.name} to ${newTagName}!`,
        ),
      );
    }

    tag.name = newTagName;
  }
  onMetaDataNameBlur(updatedMetadataItem: { id: number; name: string }, event) {
    console.log(
      `tenant-settings->onMetaDataNameBlur`,
      updatedMetadataItem,
      event,
    );
    const newMetaDataName = event.target.innerText;
    this.updateMetadataChanges.push(
      new UpdateMetaDataChangeToApprove(
        `Metadata Name Renamed From ${updatedMetadataItem.name} to ${newMetaDataName}!`,
      ),
    );
    updatedMetadataItem.name = newMetaDataName;
    const existingUpdateMetadataEntry = this.updatedMetaDataNames.find(
      (m) => m.id === updatedMetadataItem.id,
    );
    if (existingUpdateMetadataEntry) {
      existingUpdateMetadataEntry.name = updatedMetadataItem.name;
    } else {
      this.updatedMetaDataNames.push(updatedMetadataItem);
    }
  }
  onQCWorkflowObjectiveDescriptionBlur(objective: QCWorkflowObjective, event) {
    const newTask = event.target.innerText.replace(/\n/g, " ");
    if (newTask.trim() === objective.task.trim()) {
      return;
    }
    if (
      this.qcWorkflowObjectivesMarkedToDelete.find(
        (t) => t.objective.task === objective.task,
      )
    ) {
      event.target.innerText = objective.task;
      return;
    }

    if (
      !this.qcWorkflowObjectivesMarkedToInsert.find(
        (t) => t.objective.task === objective.task,
      )
    ) {
      this.qcWorkflowObjectivesMarkedToUpdate.push(
        new UpdateQCWorkflowObjectiveChangeToApprove(
          objective,
          `Update QC Workflow Objective From ${objective.task} to ${newTask}!`,
        ),
      );
      event.target.innerText = newTask;
    }

    objective.task = newTask;
    this.changeDetectorRef.markForCheck();
  }
  async onAddNewAIDetection() {
    if (!this.tenantSettings.ContentControlAIdetectionsSettings) {
      this.tenantSettings.ContentControlAIdetectionsSettings =
        [] as ContentControlAIdetectionsSettings[];
    }

    const newDetectionClass: IAIObjectDetectionClass = {
      class_name: 'New Object Detection Model',
      config: AIVideoAnalyzeConfig.DEFAULT()
    };

      const newDetectionSettings = new ContentControlAIdetectionsSettings(
        this._commonUtils.generateGuid(),
        "object_detection",
        newDetectionClass,
        false,
        false,
        ContentControlledEventActionEnum.Empty,
        new ContentControlledEditingDirectiveInstructions(),
      );
      (newDetectionSettings as any).expanded = false;
      this.tenantSettings.ContentControlAIdetectionsSettings.push(newDetectionSettings);
      this.pushTenantSettingsChangeToApprove(
        new UpdateTenantSettingChangeToApprove(
          TenantSettingsEnum.ContentControlAIdetectionsSettings,
        ),
      );
      this.changeDetectorRef.markForCheck();

  }
  canAddMoreJobs(): boolean {
    return true;
  }
  onContentControlAutomaticMitigationActionChanged(
    detection: ContentControlAIdetectionsSettings,
    action: number,
  ) {
    detection.automaticMitigationAction = action;
    switch (action) {
      case ContentControlledEventActionEnum.BlurOval:
      case ContentControlledEventActionEnum.BlurRect:
      case ContentControlledEventActionEnum.BlurScreen: {
        detection.instructions =
          ContentControlledEditingDirectiveInstructions.getBlurInstructions();
        break;
      }
      case ContentControlledEventActionEnum.PixelateOval:
      case ContentControlledEventActionEnum.PixelateRect:
      case ContentControlledEventActionEnum.PixelateScreen: {
        detection.instructions =
          ContentControlledEditingDirectiveInstructions.getPixelateInstructions();
        break;
      }
      case ContentControlledEventActionEnum.Beep: {
        detection.instructions =
          ContentControlledEditingDirectiveInstructions.getBeepFrequencyInstructions();
        break;
      }
      default: {
        detection.instructions =
          new ContentControlledEditingDirectiveInstructions();
        break;
      }
    }
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ContentControlAIdetectionsSettings,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }
  async onDeleteAIDetection(detection: ContentControlAIdetectionsSettings) {
    const result = await this._dialogService.openDialog(this.viewContainerRef, {
      type: "warning",
      title: "Remove detection settings",
      content: `Are you sure you want to remove <strong>${detection.requiredClass.class_name.toUpperCase()}</strong> detection?`,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
    });
    if (result) {
      this.tenantSettings.ContentControlAIdetectionsSettings =
        this.tenantSettings.ContentControlAIdetectionsSettings.filter(
          (d) => d !== detection,
        );

      this.pushTenantSettingsChangeToApprove(
        new UpdateTenantSettingChangeToApprove(
          TenantSettingsEnum.ContentControlAIdetectionsSettings,
        ),
      );
      this.changeDetectorRef.markForCheck();
    }
  }
  toggleContentControlAIDetectionIsEnable(
    detection: ContentControlAIdetectionsSettings,
    event: MatCheckboxChange,
  ) {
    detection.isEnabled = event.checked;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ContentControlAIdetectionsSettings,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }
  toggleContentControlAIDetectionIsAutomatic(
    detection: ContentControlAIdetectionsSettings,
    event: MatCheckboxChange,
  ) {
    detection.isAutomaticMitigation = event.checked;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ContentControlAIdetectionsSettings,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }
  toggleExpandRow(detection: any) {
    detection.expanded = !detection.expanded;
  }
  updateDetectionCertantyLmitValue(
    detection: ContentControlAIdetectionsSettings,
    value: number,
  ) {
    detection.requiredClass.config.certainty_limit = value / 100;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.ContentControlAIdetectionsSettings,
      ),
    );

    this.changeDetectorRef.markForCheck();
  }
  onDisplayContentControlStudioTimeInFramesChanged(event: Event): void {
    event.preventDefault();
    this.tenantSettings.DisplayContentControlStudioTimeInFrames =
      !this.tenantSettings.DisplayContentControlStudioTimeInFrames;

    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.DisplayContentControlStudioTimeInFrames,
      ),
    );

    this.changeDetectorRef.markForCheck();
  }
  onFirstDayOfWorkWeekChanged(value: number) {
    this.tenantSettings.FirstDayOfWorkWeek = value;
    this.pushTenantSettingsChangeToApprove(
      new UpdateTenantSettingChangeToApprove(
        TenantSettingsEnum.FirstDayOfWorkWeek,
      ),
    );
    this.changeDetectorRef.markForCheck();
  }
  private async _handleTenantSettingsUpdate(): Promise<ExecuteActionResult[]> {
    const result: ExecuteActionResult[] = [];
    if (this.tenantSettingsChangesToApprove.length > 0) {
      if (this.QCWorkflowObjectivesChangesToApprove.length > 0) {
        await this._handleQCWorkflowObjectivesUpdate();
      }

      const setTenantSettingsResult =
        await this.tenantSettingsService.setTenantSettings(this.tenantSettings);

      for (let updateTenantSettingsChange of this
        .tenantSettingsChangesToApprove) {
        if (setTenantSettingsResult) {
          result.push(
            new ExecuteActionResult(updateTenantSettingsChange, "Success!"),
          );
        } else {
          result.push(
            new ExecuteActionResult(
              updateTenantSettingsChange,
              "General Error!!",
            ),
          );
        }
      }

      this.updateTenantSettingsChanges = [];
      this.clearQCWorkflowObjectivesTempArrays();
    }

    return result;
  }
  private async _handleQCWorkflowObjectivesUpdate(): Promise<void> {
    for (let qcWorkflowObjective of this.QCWorkflowObjectivesChangesToApprove) {
      switch (qcWorkflowObjective.actionType) {
        case ETenantSettingActionType.DeleteQCWorkflowObjective: {
          const item = this.tenantSettings.QCWorkflowObjectives.find(
            (x) => x.id === qcWorkflowObjective.sourceId,
          );
          if (item) {
            item.isDeleted = true;
          }
          break;
        }
      }
    }

    this.tenantSettings.QCWorkflowObjectives =
      this.editableQCWorkflowObjectives;
  }

  private async _handleUpdateMetadata(): Promise<ExecuteActionResult[]> {
    const result: ExecuteActionResult[] = [];

    if (this.updateMetadataChanges.length > 0) {
      const metadata = this.tenantMetadata
        .filter(
          (x) =>
            x.id === -1 &&
            this.metaDataMarkedToDelete.findIndex(
              (y) => y.name.toUpperCase() === x.name.toUpperCase(),
            ) < 0,
        )
        .map((x) => x.name);
      const deletedMetadata = this.metaDataMarkedToDelete
        .filter((x) => x.id !== -1)
        .map((x) => x.id);
      const updateMetadataNamesResult =
        await this.userRepository.updateMetadataNames(
          metadata,
          deletedMetadata,
          this.updatedMetaDataNames,
        );

      for (let updateMetadataChange of this.updateMetadataChanges) {
        result.push(
          new ExecuteActionResult(
            updateMetadataChange,
            updateMetadataNamesResult ? "Success!" : "General Error!!",
          ),
        );
      }
      if (updateMetadataNamesResult) {
        this.tenantMetadata = updateMetadataNamesResult;
        this.metaDataMarkedToDelete = [];
        this.updatedMetaDataNames = [];
        this.updateMetadataChanges = [];
      }
    }

    return result;
  }

  private async _handleTagsCRUD(): Promise<ExecuteActionResult[]> {
    const result: ExecuteActionResult[] = [];

    try {
      for (let i = 0; i < this.tagsMarkedToInsert.length; i++) {
        const tagInsetChange = this.tagsMarkedToInsert[i];
        try {
          const newTag = await this.tagRepository.CreateGeneralTag(
            tagInsetChange.tag.name,
          );
          this.availableTags.push(newTag);
          this.tagsMarkedToInsert.splice(i, 1);
          i--;
          result.push(new ExecuteActionResult(tagInsetChange, "Success!"));
        } catch (e) {
          //this._showTagError(tagInsetChange.tag, "creating", null);
          result.push(
            new ExecuteActionResult(
              tagInsetChange,
              "Error! See console for more details...",
            ),
          );
          console.log(
            `tenant-settings->_handleTagsCRUD->creating->ERROR`,
            tagInsetChange,
            e,
          );
        }
      }

      for (let i = 0; i < this.tagsMarkedToUpdate.length; i++) {
        const tagUpdateChange = this.tagsMarkedToUpdate[i];
        const updateTagResponse = await this.tagRepository.Update(
          tagUpdateChange.tag,
        );
        if (updateTagResponse.success) {
          this.tagsMarkedToUpdate.splice(i, 1);
          i--;
          result.push(new ExecuteActionResult(tagUpdateChange, "Success!"));
        } else {
          //this._showTagError(tag, "updating", updateTagResponse?.dependencies);
          if (updateTagResponse?.dependencies?.length > 0) {
            for (let dependency of updateTagResponse.dependencies) {
              result.push(
                new ExecuteActionResult(
                  tagUpdateChange,
                  `Dependency conflict in ${dependency.sourceType} ${dependency.sourceName}!`,
                ),
              );
            }
          }

          //return false;
        }
      }

      for (let i = 0; i < this.tagsMarkedToDelete.length; i++) {
        const tagDeleteChange = this.tagsMarkedToDelete[i];
        const deleteTagResponse = await this.tagRepository.Delete(
          tagDeleteChange.tag,
        );
        if (deleteTagResponse.success) {
          this.tagsMarkedToDelete.splice(i, 1);
          this.availableTags.splice(
            this.availableTags.indexOf(tagDeleteChange.tag),
            1,
          );
          i--;
          result.push(new ExecuteActionResult(tagDeleteChange, "Success!"));
        } else {
          //this._showTagError(tag, "deleting", deleteTagResponse?.dependencies);
          //return false;

          if (deleteTagResponse?.dependencies?.length > 0) {
            for (let dependency of deleteTagResponse.dependencies) {
              result.push(
                new ExecuteActionResult(
                  tagDeleteChange,
                  `Dependency conflict in ${dependency.sourceType} ${dependency.sourceName}!`,
                ),
              );
            }
          }
        }
      }

      return result;
    } catch (e) {
      console.error("tenant-settings->_handleTagsCRUD->ERROR", e);
      throw e;
    }
  }

  private _showResultPopup(results: ExecuteActionResult[]): void {
    this.ui.notify(this.ui.EVENTS.ShowAlert, {
      title: "Save Changes Result",
      message: this._sanitizer.bypassSecurityTrustHtml(
        this._initSaveChangesResultTableHtml(results),
      ),
    });
  }
  private clearQCWorkflowObjectivesTempArrays() {
    this.qcWorkflowObjectivesMarkedToDelete = [];
    this.qcWorkflowObjectivesMarkedToUpdate = [];
    this.qcWorkflowObjectivesMarkedToInsert = [];
    this.availableQCWorkflowObjectives =
      this.tenantSettings.QCWorkflowObjectives;
  }

  private _initSaveChangesResultTableHtml(
    results: ExecuteActionResult[],
  ): string {
    let resultHtml: string[] = [];
    if (results && results.length > 0) {
      resultHtml.push('<table style="width:100%">');
      resultHtml.push(
        '<tr><th style="border: 1px solid black">Action</th><th  style="border: 1px solid black">Result</th></tr>',
      );

      for (const result of results) {
        resultHtml.push(
          `<tr><td  style="border: 1px solid black">${result.ChangeToApprove.message}</td><td  style="border: 1px solid black; color: ${result.resultMessage === "Success!" ? "green" : "red"}">${result.resultMessage}</td></tr>`,
        );
      }
      resultHtml.push("</table>");
    }
    return resultHtml.join("");
  }

  protected readonly EAiModelType = EAiModelType;
  protected readonly ContentControlledEventActionEnum = ContentControlledEventActionEnum;
  protected readonly TenantSettingsEnum = TenantSettingsEnum;
  protected readonly UpdateTenantSettingChangeToApprove = UpdateTenantSettingChangeToApprove;
}
